import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import Icon from "../../UI/Icon";
import Modal from "../../UI/Modal";
import Breadcrumb from "../../UI/Breadcrumb";
import ProductGrid from "../../UI/ProductGrid";
import { Button, EmptyState, LoadError, Skeleton, isPrerender } from "../../UI/Primitives";
import SortMenu from "./SortMenu";
import {
  SORTS,
  applyFilters,
  countBy,
  parseFilters,
  priceBuckets,
  serializeFilters,
  sortProducts,
} from "./catalogFilters";
import { getProductsByCategory, getSubCategories, productPath, searchProducts } from "../../../Service/catalog";
import * as endpoint from "../../../Service/endpoints";
import { NAV_CATEGORIES, getCategoryMeta } from "./categoryMeta";
import { canonicalUrl } from "../../../Service/seoUrl";
import "./catalog.css";

const PAGE_SIZE = 12;
const VIEW_KEY = "voorent-listing-view";

function readView() {
  try {
    return localStorage.getItem(VIEW_KEY) === "rows" ? "rows" : "grid";
  } catch (e) {
    return "grid";
  }
}

function FacetGroup({ title, children }) {
  return (
    <fieldset className="v-facet">
      <legend className="v-facet__title">{title}</legend>
      <div className="v-facet__body">{children}</div>
    </fieldset>
  );
}

function FilterPanel({ facets, filters, activeSubs, onToggleSub, onToggleCat, onTogglePrice, onToggle }) {
  return (
    <div className="v-filters">
      {facets.subs.length > 1 || activeSubs.length ? (
        <FacetGroup title="Type">
          {facets.subs.map((sub) => (
            <label key={sub.slug} className="v-check">
              <input type="checkbox" checked={activeSubs.includes(sub.slug)} onChange={() => onToggleSub(sub.slug)} />
              <span>{sub.name}</span>
              <span className="v-check__count">{sub.count}</span>
            </label>
          ))}
        </FacetGroup>
      ) : null}

      {facets.cats.length > 1 ? (
        <FacetGroup title="Category">
          {facets.cats.map((cat) => (
            <label key={cat.slug} className="v-check">
              <input type="checkbox" checked={filters.cat.includes(cat.slug)} onChange={() => onToggleCat(cat.slug)} />
              <span>{cat.name}</span>
              <span className="v-check__count">{cat.count}</span>
            </label>
          ))}
        </FacetGroup>
      ) : null}

      {facets.prices.length > 1 ? (
        <FacetGroup title={facets.priceTitle}>
          {facets.prices.map((bucket) => (
            <label key={bucket.id} className={`v-check ${bucket.count === 0 && !filters.price.includes(bucket.id) ? "is-empty" : ""}`}>
              <input
                type="checkbox"
                checked={filters.price.includes(bucket.id)}
                onChange={() => onTogglePrice(bucket.id)}
              />
              <span>{bucket.label}</span>
              <span className="v-check__count">{bucket.count}</span>
            </label>
          ))}
        </FacetGroup>
      ) : null}

      {facets.hasOutOfStock || facets.hasExpress ? (
        <FacetGroup title="Availability">
          {facets.hasOutOfStock ? (
            <label className="v-check">
              <input type="checkbox" checked={filters.avail} onChange={() => onToggle("avail")} />
              <span>In stock only</span>
              <span className="v-check__count">{facets.inStockCount}</span>
            </label>
          ) : null}
          {facets.hasExpress ? (
            <label className="v-check">
              <input type="checkbox" checked={filters.express} onChange={() => onToggle("express")} />
              <span>Express delivery</span>
              <span className="v-check__count">{facets.expressCount}</span>
            </label>
          ) : null}
        </FacetGroup>
      ) : null}
    </div>
  );
}

/**
 * Shared catalogue listing for category, sub-category and search pages.
 * All filters are client-side over the list returned by the existing APIs and
 * are mirrored in the URL (?sub=…&price=…&avail=1&sort=…) so views are shareable.
 */
function ProductListing({ mode, category, initialSub, query, heading, description, crumbs }) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const city = endpoint.getCurrentCity();
  const cityLabel = city.charAt(0).toUpperCase() + city.slice(1);
  const categories = city === "gurgaon" ? [...NAV_CATEGORIES, "expressDelivery"] : NAV_CATEGORIES;

  const [data, setData] = useState({ status: "loading", products: [], subs: [] });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [view, setView] = useState(readView);

  const filters = useMemo(() => parseFilters(location.search), [location.search]);
  const activeSubs = useMemo(
    () => (filters.sub !== null ? filters.sub : initialSub ? [initialSub] : []),
    [filters.sub, initialSub]
  );

  const load = useCallback(() => {
    setData((prev) => ({ ...prev, status: "loading" }));
    const products = mode === "search" ? searchProducts(query) : getProductsByCategory(category);
    const subs = mode === "category" ? getSubCategories(category).catch(() => []) : Promise.resolve([]);
    Promise.all([products, subs]).then(
      ([productList, subList]) => setData({ status: "done", products: productList, subs: subList }),
      () => setData({ status: "error", products: [], subs: [] })
    );
  }, [mode, query, category]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setVisible(PAGE_SIZE), [location.search]);

  const update = (patch) => {
    const next = { ...filters, sub: filters.sub !== null ? filters.sub : initialSub ? [initialSub] : null, ...patch };
    history.replace(`${location.pathname}${serializeFilters(next)}`);
  };

  const toggleIn = (listValue, value) =>
    listValue.includes(value) ? listValue.filter((v) => v !== value) : [...listValue, value];

  const onToggleSub = (slug) => update({ sub: toggleIn(activeSubs, slug) });
  const onToggleCat = (slug) => update({ cat: toggleIn(filters.cat, slug) });
  const onTogglePrice = (id) => update({ price: toggleIn(filters.price, id) });
  const onToggle = (key) => update({ [key]: !filters[key] });
  const clearAll = () =>
    history.replace(`${location.pathname}${serializeFilters({ sub: initialSub ? [] : null, cat: [], price: [], avail: false, express: false, sort: filters.sort })}`);

  const products = data.products;
  const results = useMemo(
    () => sortProducts(applyFilters(products, filters, activeSubs), filters.sort),
    [products, filters, activeSubs]
  );

  // Facet options + counts (each facet's counts respect the other active filters).
  const facets = useMemo(() => {
    const forSubs = applyFilters(products, filters, []);
    const subCounts = countBy(forSubs, "subCategory");
    const subNames = {};
    data.subs.forEach((s) => {
      subNames[s.slug] = s.name;
    });
    products.forEach((p) => {
      if (p.subCategory && !subNames[p.subCategory.slug]) subNames[p.subCategory.slug] = p.subCategory.name;
    });
    const productSubs = new Set(products.map((p) => p.subCategory && p.subCategory.slug).filter(Boolean));
    const subs = Object.keys(subNames)
      .filter((slug) => productSubs.has(slug) || activeSubs.includes(slug))
      .map((slug) => ({ slug, name: subNames[slug], count: subCounts[slug] || 0 }));

    const forCats = applyFilters(products, { ...filters, cat: [] }, activeSubs);
    const catCounts = countBy(forCats, "category");
    const catNames = {};
    products.forEach((p) => {
      if (p.category) catNames[p.category.slug] = p.category.name;
    });
    const cats =
      mode === "search"
        ? Object.keys(catNames).map((slug) => ({ slug, name: catNames[slug], count: catCounts[slug] || 0 }))
        : [];

    const forPrice = applyFilters(products, { ...filters, price: [] }, activeSubs);
    const forAvail = applyFilters(products, { ...filters, avail: false, express: false }, activeSubs);
    const allSeasonal = products.length > 0 && products.every((p) => p.category && p.category.slug === "seasonal");

    return {
      subs,
      cats,
      prices: priceBuckets(products, forPrice),
      priceTitle: allSeasonal ? "Price per season" : "Monthly rent",
      hasOutOfStock: products.some((p) => p.status === "Out of Stock"),
      inStockCount: forAvail.filter((p) => p.status !== "Out of Stock").length,
      hasExpress: city === "gurgaon" && products.some((p) => p.instantDelivery),
      expressCount: forAvail.filter((p) => p.instantDelivery).length,
    };
  }, [products, data.subs, filters, activeSubs, mode, city]);

  const chips = [];
  activeSubs.forEach((slug) => {
    const sub = facets.subs.find((s) => s.slug === slug);
    chips.push({ key: `sub-${slug}`, label: sub ? sub.name : slug, onRemove: () => onToggleSub(slug) });
  });
  filters.cat.forEach((slug) => {
    const cat = facets.cats.find((c) => c.slug === slug);
    chips.push({ key: `cat-${slug}`, label: cat ? cat.name : slug, onRemove: () => onToggleCat(slug) });
  });
  filters.price.forEach((id) => {
    const bucket = facets.prices.find((b) => b.id === id);
    chips.push({ key: `price-${id}`, label: bucket ? bucket.label : id, onRemove: () => onTogglePrice(id) });
  });
  if (filters.avail) chips.push({ key: "avail", label: "In stock only", onRemove: () => onToggle("avail") });
  if (filters.express) chips.push({ key: "express", label: "Express delivery", onRemove: () => onToggle("express") });

  const hasPopular = products.some((p) => p.isPopular || p.isTrending);
  const sortOptions = Object.keys(SORTS)
    .filter((key) => key !== "popular" || hasPopular)
    .map((key) => ({ value: key, label: SORTS[key].label }));

  const setViewMode = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch (e) {
      // ignore
    }
  };

  const loading = data.status === "loading" || (data.status === "error" && isPrerender());
  const shown = results.slice(0, visible);
  // Sub-category pages get their display name from the API list when available.
  const subMatch = initialSub ? data.subs.find((s) => s.slug === initialSub) : null;
  const subName = subMatch ? subMatch.name : null;
  const title = typeof heading === "function" ? heading(subName) : heading;
  const trail = typeof crumbs === "function" ? crumbs(subName) : crumbs;
  const filterCount = chips.length;

  const panel = (
    <FilterPanel
      facets={facets}
      filters={filters}
      activeSubs={activeSubs}
      onToggleSub={onToggleSub}
      onToggleCat={onToggleCat}
      onTogglePrice={onTogglePrice}
      onToggle={onToggle}
    />
  );

  const itemList =
    data.status === "done" && results.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: results.slice(0, 24).map((p, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: canonicalUrl(productPath(p) || "/"),
            name: p.name,
          })),
        }
      : null;

  return (
    <main id="main-content" className={`v-main v-listing v-listing--${mode}`}>
      {itemList ? (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(itemList)}</script>
        </Helmet>
      ) : null}

      <div className="v-container">
        <Breadcrumb items={trail} />

        <header className="v-listing__head">
          <div>
            <h1 className="v-listing__title">{title}</h1>
            {description ? <p className="v-listing__desc">{description}</p> : null}
          </div>
          <button type="button" className="v-location v-listing__city" onClick={() => dispatch({ type: "user.city.show" })}>
            <Icon name="pin" size={16} />
            <span>{cityLabel}</span>
            <Icon name="chevronDown" size={14} className="v-location__chev" />
          </button>
        </header>

        {mode === "category" ? (
          <nav className="v-pills" aria-label="Categories">
            {categories.map((slug) => (
              <Link
                key={slug}
                to={endpoint.getPath(slug)}
                className="v-chip"
                aria-current={slug === category ? "page" : undefined}
              >
                {getCategoryMeta(slug).short}
              </Link>
            ))}
          </nav>
        ) : null}

        {!loading && data.status === "done" ? (
          <p className="v-listing__count-mobile" aria-hidden="true">
            <strong>{results.length}</strong> {results.length === 1 ? "product" : "products"}
          </p>
        ) : null}

        <div className="v-listing__layout">
          <aside className="v-listing__aside" aria-label="Filters">
            <div className="v-listing__aside-head">
              <h2>Filters</h2>
              {filterCount ? (
                <button type="button" className="v-link v-link--button" onClick={clearAll}>
                  Clear all
                </button>
              ) : null}
            </div>
            {loading ? (
              <div className="v-filters">
                {[0, 1, 2].map((n) => (
                  <div key={n} className="v-facet">
                    <Skeleton width="40%" height="12px" />
                    <Skeleton width="80%" height="14px" style={{ marginTop: 16 }} />
                    <Skeleton width="65%" height="14px" style={{ marginTop: 14 }} />
                    <Skeleton width="72%" height="14px" style={{ marginTop: 14 }} />
                  </div>
                ))}
              </div>
            ) : (
              panel
            )}
          </aside>

          <section className="v-listing__results" aria-labelledby="v-results-count">
            <div className="v-toolbar">
              <p id="v-results-count" className="v-toolbar__count" aria-live="polite">
                {loading ? (
                  <Skeleton width="90px" height="14px" />
                ) : (
                  <>
                    <strong>{results.length}</strong> {results.length === 1 ? "product" : "products"}
                  </>
                )}
              </p>
              <div className="v-toolbar__actions">
                <button type="button" className="v-toolbar-btn v-toolbar__filter" onClick={() => setSheetOpen(true)} aria-haspopup="dialog">
                  <Icon name="sliders" size={18} />
                  Filter
                  {filterCount ? <span className="v-toolbar-btn__count">{filterCount}</span> : null}
                </button>
                <SortMenu value={filters.sort} options={sortOptions} onChange={(sort) => update({ sort })} />
                <div className="v-view-toggle" role="group" aria-label="Layout">
                  <button type="button" className="v-icon-btn" aria-pressed={view === "grid"} aria-label="Grid view" onClick={() => setViewMode("grid")}>
                    <Icon name="grid" size={18} />
                  </button>
                  <button type="button" className="v-icon-btn" aria-pressed={view === "rows"} aria-label="List view" onClick={() => setViewMode("rows")}>
                    <Icon name="rows" size={18} />
                  </button>
                </div>
              </div>
            </div>

            {chips.length ? (
              <div className="v-chip-row v-listing__chips">
                {chips.map((chip) => (
                  <button key={chip.key} type="button" className="v-chip v-chip--removable" onClick={chip.onRemove} aria-label={`Remove filter ${chip.label}`}>
                    {chip.label}
                    <span className="v-chip__x" aria-hidden="true">
                      <Icon name="close" size={14} />
                    </span>
                  </button>
                ))}
                <button type="button" className="v-link v-link--button" onClick={clearAll}>
                  Clear all
                </button>
              </div>
            ) : null}

            {data.status === "error" && !loading ? (
              <LoadError onRetry={load} />
            ) : loading ? (
              <ProductGrid loading skeletonCount={9} layout={view} />
            ) : products.length === 0 ? (
              <EmptyState
                kind="empty"
                title={mode === "search" ? `No results for “${query}”` : "Nothing here just yet"}
                description={
                  mode === "search"
                    ? "Check the spelling or try a broader word like “sofa”, “bed” or “fridge”."
                    : "We don't have products listed in this category right now. Explore our other collections."
                }
                actions={
                  <>
                    <Button to={endpoint.getPath("furniture")} variant="primary">
                      Browse furniture
                    </Button>
                    <Button to={endpoint.getPath("appliance")} variant="secondary">
                      Browse appliances
                    </Button>
                  </>
                }
              />
            ) : results.length === 0 ? (
              <EmptyState
                kind="empty"
                title="No products match these filters"
                description="Try removing a filter or two to see more options."
                actions={
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <>
                <ProductGrid products={shown} layout={view} priorityCount={view === "rows" ? 2 : 3} label={title} />
                {results.length > visible ? (
                  <div className="v-more">
                    <p className="v-more__meta">
                      Showing {shown.length} of {results.length}
                    </p>
                    <div className="v-more__bar" aria-hidden="true">
                      <span style={{ width: `${(shown.length / results.length) * 100}%` }} />
                    </div>
                    <Button variant="secondary" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                      Load more
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
      </div>

      <Modal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Filters"
        variant="sheet"
        className="v-filter-sheet"
        footer={
          <>
            <Button variant="secondary" onClick={clearAll} disabled={!filterCount}>
              Clear all
            </Button>
            <Button variant="primary" block onClick={() => setSheetOpen(false)}>
              Show {results.length} {results.length === 1 ? "product" : "products"}
            </Button>
          </>
        }
      >
        {panel}
      </Modal>
    </main>
  );
}

export default ProductListing;
